/**
 * CBAMExposureWidget — Phase 7.1 (Intelligence Engine).
 *
 * Rendered inside the /workspace grid for CFO / ESG / Admin personas.
 * Reads real data from:
 *   GET  /api/cbam/summary
 *   POST /api/cbam/calculate
 *
 * The widget is intentionally self-contained: it pulls its own summary once,
 * exposes an inline calculator (product picker + fields), and updates the
 * total after each calculation. No prop drilling required.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, AlertTriangle, TrendingUp, Factory, Sparkles, RefreshCw, Info } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const INDUSTRIES = ['Steel', 'Cement', 'Aluminium', 'Chemicals', 'Fertilizer', 'Hydrogen', 'Electricity'];
const COUNTRIES = ['China', 'Turkey', 'India', 'Russia', 'Brazil', 'Ukraine', 'Serbia', 'South Korea', 'Vietnam', 'Egypt', 'Morocco', 'United States', 'Germany', 'Other'];

const RISK_COLORS = {
  low:      'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
  medium:   'bg-amber-500/10  text-amber-600  border-amber-500/30',
  high:     'bg-orange-500/10 text-orange-600 border-orange-500/30',
  critical: 'bg-red-500/10    text-red-600    border-red-500/30',
};

function fmtEur(v) {
  const n = Number(v || 0);
  if (n >= 1_000_000) return `€${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000)     return `€${(n / 1_000).toFixed(1)}k`;
  return `€${n.toFixed(0)}`;
}

export default function CBAMExposureWidget({ d }) {
  const products = d?.products || [];
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCalc, setShowCalc] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [form, setForm] = useState({
    product_id: '',
    industry: 'Steel',
    country_of_origin: 'China',
    import_volume: '',
    carbon_intensity: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/cbam/summary');
      setSummary(r.data);
    } catch (err) {
      // 400 = no organization yet; render an empty state gracefully.
      setSummary({ total_exposure_eur: 0, unique_products: 0, top_products: [], by_risk: {} });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    // Preselect the first product once the workspace data lands
    if (!form.product_id && products.length) {
      setForm((f) => ({ ...f, product_id: products[0].id }));
    }
  }, [products, form.product_id]);

  const handleCalculate = async () => {
    if (!form.product_id) { toast.error('Select a product first'); return; }
    if (!form.import_volume || !form.carbon_intensity) {
      toast.error('Import volume and carbon intensity are required.');
      return;
    }
    setCalculating(true);
    try {
      const r = await api.post('/cbam/calculate', {
        product_id: form.product_id,
        industry: form.industry,
        country_of_origin: form.country_of_origin,
        import_volume: parseFloat(form.import_volume),
        carbon_intensity: parseFloat(form.carbon_intensity),
      });
      const cost = r.data?.estimated_cbam_cost || 0;
      toast.success(`Exposure calculated · ${fmtEur(cost)} — ${r.data?.risk_level}`);
      setShowCalc(false);
      setForm((f) => ({ ...f, import_volume: '', carbon_intensity: '' }));
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Calculation failed');
    } finally { setCalculating(false); }
  };

  const criticalCount = (summary?.by_risk?.critical || 0) + (summary?.by_risk?.high || 0);

  const summaryBody = useMemo(() => {
    if (!summary) return null;
    return (
      <>
        <div className="flex items-baseline gap-3 flex-wrap" data-testid="cbam-summary-total">
          <div className="text-3xl font-heading font-bold text-red-600">
            {fmtEur(summary.total_exposure_eur)}
          </div>
          <div className="text-xs text-muted-foreground">
            projected annual CBAM cost · {summary.unique_products || 0} product{summary.unique_products === 1 ? '' : 's'}
          </div>
        </div>
        <div className="grid grid-cols-4 gap-2 mt-3 text-center text-[11px]">
          {['critical', 'high', 'medium', 'low'].map((k) => (
            <div key={k} className={`rounded-md border py-1.5 px-1 ${RISK_COLORS[k]}`} data-testid={`cbam-risk-${k}`}>
              <div className="font-heading font-bold text-base leading-none">{summary.by_risk?.[k] || 0}</div>
              <div className="uppercase tracking-wide mt-0.5">{k}</div>
            </div>
          ))}
        </div>
        {summary.top_products?.length > 0 && (
          <div className="mt-3 border-t pt-2 space-y-1.5">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
              <TrendingUp className="w-3 h-3" /> Highest exposure
            </div>
            {summary.top_products.slice(0, 3).map((p) => (
              <div key={p.id} className="flex items-center justify-between text-xs" data-testid={`cbam-top-${p.product_id}`}>
                <div className="truncate max-w-[60%]">
                  <span className="font-medium">{p.product_name}</span>
                  <span className="text-muted-foreground"> · {p.country_of_origin}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded border ${RISK_COLORS[p.risk_level]}`}>
                    {p.risk_level}
                  </span>
                  <span className="font-mono font-semibold">{fmtEur(p.estimated_cbam_cost)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="mt-3 flex items-center justify-between text-[11px] text-muted-foreground border-t pt-2">
          <span className="flex items-center gap-1"><Info className="w-3 h-3" />
            {summary.current_year} price: €{Number(summary.current_price || 0).toFixed(0)}/tCO₂e
          </span>
          <button onClick={load} className="hover:text-foreground flex items-center gap-1" data-testid="cbam-refresh-btn">
            <RefreshCw className="w-3 h-3" /> refresh
          </button>
        </div>
      </>
    );
  }, [summary, load]);

  return (
    <Card className="p-5" data-testid="wgt-cbam-exposure">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center">
            <Factory className="w-4 h-4 text-red-600" />
          </div>
          <div>
            <div className="font-heading font-semibold text-sm">CBAM Exposure</div>
            <div className="text-[11px] text-muted-foreground">Border-tax liability · live calculator</div>
          </div>
        </div>
        <Button
          size="sm"
          variant={showCalc ? 'outline' : 'default'}
          onClick={() => setShowCalc((v) => !v)}
          data-testid="cbam-toggle-calc-btn"
        >
          {showCalc ? 'Close' : (
            <><Sparkles className="w-3.5 h-3.5 mr-1" /> {summary?.unique_products ? 'New calc' : 'Calculate'}</>
          )}
        </Button>
      </div>

      {criticalCount > 0 && !showCalc && (
        <div
          className="mb-3 flex items-start gap-2 rounded-md border border-red-500/30 bg-red-500/5 p-2 text-xs text-red-700 dark:text-red-300"
          data-testid="cbam-critical-banner"
        >
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>{criticalCount} product{criticalCount === 1 ? '' : 's'} at high/critical exposure — engage compliance before Q3.</div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-8 text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading exposure…
        </div>
      ) : showCalc ? (
        <div className="space-y-3" data-testid="cbam-calculator">
          <div>
            <Label className="text-xs">Product</Label>
            <Select value={form.product_id} onValueChange={(v) => setForm({ ...form, product_id: v })}>
              <SelectTrigger data-testid="cbam-product-select"><SelectValue placeholder="Select product" /></SelectTrigger>
              <SelectContent>
                {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                {!products.length && <SelectItem value="__none__" disabled>No products yet</SelectItem>}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Industry</Label>
              <Select value={form.industry} onValueChange={(v) => setForm({ ...form, industry: v })}>
                <SelectTrigger data-testid="cbam-industry-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Country of origin</Label>
              <Select value={form.country_of_origin} onValueChange={(v) => setForm({ ...form, country_of_origin: v })}>
                <SelectTrigger data-testid="cbam-country-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Import volume (t/yr)</Label>
              <Input type="number" min="0" step="0.1"
                     value={form.import_volume}
                     onChange={(e) => setForm({ ...form, import_volume: e.target.value })}
                     placeholder="e.g. 5000"
                     data-testid="cbam-volume-input" />
            </div>
            <div>
              <Label className="text-xs">Intensity (tCO₂e/t)</Label>
              <Input type="number" min="0" step="0.01"
                     value={form.carbon_intensity}
                     onChange={(e) => setForm({ ...form, carbon_intensity: e.target.value })}
                     placeholder="e.g. 1.8"
                     data-testid="cbam-intensity-input" />
            </div>
          </div>
          <Button
            className="w-full bg-red-600 hover:bg-red-700 text-white"
            onClick={handleCalculate}
            disabled={calculating}
            data-testid="cbam-calculate-btn"
          >
            {calculating
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Calculating…</>
              : <>Calculate exposure</>}
          </Button>
          <div className="text-[10px] text-muted-foreground text-center">
            Formula: volume × intensity × €{Number(summary?.current_price || 85).toFixed(0)}/tCO₂e
          </div>
        </div>
      ) : summary?.total_exposure_eur > 0 ? (
        summaryBody
      ) : (
        <div className="text-center py-6 space-y-2">
          <div className="text-sm text-muted-foreground">No CBAM exposure calculated yet.</div>
          <div className="text-xs text-muted-foreground">Run a calculation to project your border-tax liability.</div>
          <Badge variant="outline" className="mt-2">EU CBAM · phase-in from 2026</Badge>
        </div>
      )}
    </Card>
  );
}
